/**
 * LiveMonitorPage — operator fleet monitor.
 *
 * A per-device VISIBILITY view for a merchant's paired kitchen tablets:
 * each device's health (online / idle / offline + last-seen) and the OTA
 * app + native version it last reported. Data comes from the
 * session-scoped GET /m/:mid/devices/fleet endpoint (api.listFleet),
 * which is tenant-isolated by the same requireMerchantSession +
 * `WHERE merchant_id=?` gate as the device list — a merchant only ever
 * sees its own fleet.
 *
 * This is deliberately an OPERATOR view, not an OTA device-management
 * console: no channel/pin controls (those stay in the platform-engineer
 * OTA admin tooling). It answers "are my tablets up, and what are they
 * running?".
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { PRODUCTS } from '@tovriktech/registry';
import { Button, Card, EmptyState, Skeleton, StatusBadge } from '@tovriktech/ui';
import { api, type FleetDevice } from '../lib/api';

export interface LiveMonitorPageProps {
  merchantId: string;
  onBack: () => void;
}

/** A currentColor tablet/device glyph — inherits the EmptyState medallion. */
function DeviceIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="2.5" width="16" height="19" rx="2.5" />
      <line x1="10.5" y1="18.5" x2="13.5" y2="18.5" />
    </svg>
  );
}

/**
 * Map the derived device health onto the canonical StatusBadge vocabulary
 * + an operator label. Green (connected) → muted (inactive) → amber
 * (action_required) ladder, so an offline tablet reads as needing
 * attention. All colors are theme tokens via StatusBadge — no raw hex.
 */
function healthBadge(status: FleetDevice['status']): { badge: 'connected' | 'inactive' | 'action_required'; label: string } {
  switch (status) {
    case 'online':
      return { badge: 'connected', label: 'Online' };
    case 'idle':
      return { badge: 'inactive', label: 'Idle' };
    default:
      // stale / revoked (revoked is filtered server-side) → offline.
      return { badge: 'action_required', label: 'Offline' };
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'never';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function LiveMonitorPage(props: LiveMonitorPageProps): ReactNode {
  const [devices, setDevices] = useState<FleetDevice[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api.listFleet(props.merchantId);
      setDevices(r.devices);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
      setDevices([]);
    }
  }, [props.merchantId]);

  useEffect(() => {
    setDevices(null);
    void refresh();
  }, [refresh]);

  const empty = devices !== null && devices.length === 0 && !loadError;
  const onlineCount = devices?.filter((d) => d.status === 'online').length ?? 0;

  return (
    <div className="flex h-full flex-col bg-tvk-canvas text-tvk-fg">
      <header className="flex items-center gap-4 border-b border-tvk-border bg-tvk-surface px-5 py-3">
        <Button type="button" variant="ghost" size="sm" onClick={props.onBack}>
          ← Devices
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-tvk-serif text-lg font-semibold leading-tight tracking-tight text-tvk-fg">
            Fleet monitor
          </h1>
          <p className="truncate text-xs leading-snug text-tvk-fg-muted">
            Health + app version for every {PRODUCTS.kitchen.display} tablet paired to{' '}
            <span className="font-medium tabular-nums text-tvk-fg">{props.merchantId}</span>
          </p>
        </div>
        {devices !== null && !loadError && (
          <StatusBadge status={onlineCount > 0 ? 'connected' : 'inactive'} className="flex-shrink-0">
            {onlineCount}/{devices.length} online
          </StatusBadge>
        )}
        <Button type="button" variant="secondary" size="sm" onClick={() => void refresh()}>
          Refresh
        </Button>
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto w-full max-w-3xl">
          {loadError && (
            <div className="mb-6 rounded-tvk-md border border-tvk-danger/30 bg-tvk-danger/10 p-3 text-xs text-tvk-danger">
              Failed to load the fleet: {loadError}
            </div>
          )}

          {devices === null && (
            <Card className="overflow-hidden" aria-busy="true" aria-live="polite">
              <span className="sr-only">Loading fleet…</span>
              <ul className="divide-y divide-tvk-border">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex items-center justify-between gap-4 px-5 py-4">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Skeleton height={14} width="40%" />
                      <Skeleton height={11} width="65%" />
                    </div>
                    <Skeleton height={24} width={72} shape="rect" />
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {empty && (
            <Card padding="lg">
              <EmptyState
                icon={<DeviceIcon />}
                title="No paired devices yet"
                description={`Pair a ${PRODUCTS.kitchen.display} tablet to start seeing its health and app version here.`}
                action={
                  <Button type="button" variant="secondary" size="sm" onClick={props.onBack}>
                    Go to devices
                  </Button>
                }
              />
            </Card>
          )}

          {devices && devices.length > 0 && (
            <Card className="overflow-hidden">
              <ul className="divide-y divide-tvk-border">
                {devices.map((d) => {
                  const health = healthBadge(d.status);
                  return (
                    <li key={d.id} className="flex items-center justify-between gap-4 px-5 py-3.5">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2.5">
                          <span className="truncate text-sm font-medium text-tvk-fg">{d.name}</span>
                          <StatusBadge status={health.badge} className="flex-shrink-0">
                            {health.label}
                          </StatusBadge>
                        </div>
                        <div className="mt-0.5 text-xs text-tvk-fg-muted">
                          Last seen {formatRelative(d.last_seen_at)} · {d.device_type}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className="text-sm tabular-nums text-tvk-fg">
                          {d.current_version ? `v${d.current_version}` : 'unknown'}
                        </div>
                        <div className="text-xs tabular-nums text-tvk-fg-muted">
                          {d.native_version ? `app ${d.native_version}` : 'app —'}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
